<template>
  <div class="menu-cascade-layer">
    <div
      v-if="activeL1"
      ref="l2Panel"
      class="menu-l2-popover cascade-layer-panel"
      :class="l2PanelClass"
      :style="l2PanelStyle"
      @mouseenter="handleL2PanelEnter"
      @mouseleave="handleL2PanelLeave">
      <div class="cascade-l2-panel">
        <ul class="list-unstyled mb-0 cascade-l2-list">
          <li
            v-for="sg in subGroups"
            :key="sg"
            class="cascade-menu-item"
            :class="{ active: hoveredSubGroup === sg || activeSubGroup === sg }"
            @mouseenter="handleL2MouseEnter(sg, $event)">
            <a class="d-flex align-items-center cascade-menu-link" href="#" @click.prevent="handleL2Click(sg)">
              <div class="cascade-menu-label flex-fill text-truncate" :title="getProductsGroupLabel(sg)">{{ getProductsGroupLabel(sg) }}</div>
              <div class="cascade-menu-arrow flex-shrink-0">
                <a-icon type="right" style="font-size: 12px;" />
              </div>
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div
      v-if="activeL1 && activeSubGroup && l3Groups.length"
      ref="l3Panel"
      class="menu-l3-popover cascade-layer-panel"
      :class="l3PanelClass"
      :style="l3PanelStyle"
      @mouseenter="handleL3PanelEnter"
      @mouseleave="handleL3PanelLeave">
      <div class="cascade-l3-panel-inner">
        <div v-for="(group, gIdx) in l3Groups" :key="gIdx" class="cascade-l3-group">
          <div class="cascade-l3-group-title text-truncate" :title="group.title">{{ group.title }}</div>
          <div class="cascade-l3-grid">
            <div
              v-for="link in group.submenus"
              :key="link.path"
              class="cascade-l3-cell">
              <div
                class="cascade-l3-link d-flex align-items-center"
                :class="{ stared: link.stared }"
                @click="handleLinkClick(link)">
                <div class="link text-truncate" :title="link.title">{{ link.title }}</div>
                <div class="icon" @click="handleStar($event, link)">
                  <icon
                    :type="link.stared ? 'stared' : 'star'"
                    :title="link.stared ? $t('common.remove_store') : $t('common.store_to_my_menus')" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { collectSubGroups, collectL3Groups, getFirstL3Link } from './menuUtils'
import {
  TOP_SAFE,
  TRIGGER_HEIGHT_L1,
  TRIGGER_HEIGHT_L2,
  SUBGROUP_ITEM_HEIGHT,
  SUBGROUP_VERTICAL_PADDING,
  CASCADE_L2_WIDTH,
  CASCADE_L3_WIDTH,
  CASCADE_MOUSE_LEAVE_DELAY,
  calcPanelTop,
  calcPanelLeftFromRect,
  applyPanelMaxHeight,
  clampPanelTop,
} from './menuPopoverPosition'

export default {
  name: 'MenuCascadeLayer',
  props: {
    getLabel: {
      type: Function,
      required: true,
    },
    showMenu: {
      type: Function,
      required: true,
    },
    light: Boolean,
  },
  data () {
    return {
      activeL1: null,
      l1TriggerEl: null,
      activeSubGroup: null,
      l2TriggerEl: null,
      hoveredSubGroup: null,
      l2Visible: false,
      l3Visible: false,
      l2Ready: false,
      l3Ready: false,
      l2Top: 0,
      l2Left: 0,
      l3Top: 0,
      l3Left: 0,
    }
  },
  computed: {
    ...mapState('common', {
      openCloudShell: state => state.openCloudShell,
      cloudShellHeight: state => state.cloudShellHeight,
      staredList: state => state.sidebar.staredList,
    }),
    subGroups () {
      if (!this.activeL1) return []
      return collectSubGroups(this.activeL1, this.showMenu)
    },
    l3Groups () {
      if (!this.activeL1 || !this.activeSubGroup) return []
      return collectL3Groups(this.activeL1, this.activeSubGroup, this.showMenu, this.getLabel, this.staredList)
    },
    l2Height () {
      return this.subGroups.length * SUBGROUP_ITEM_HEIGHT + SUBGROUP_VERTICAL_PADDING
    },
    l3Height () {
      let rowCount = 0
      this.l3Groups.forEach(group => {
        rowCount += 1 + Math.ceil(group.submenus.length / 3)
      })
      return rowCount * TRIGGER_HEIGHT_L2 + 24
    },
    cloudShellOffset () {
      return this.openCloudShell ? this.cloudShellHeight : 0
    },
    l2PanelClass () {
      let classname = ''
      if (this.light) {
        classname += ' light-theme'
      }
      if (this.l2Visible && !this.l2Ready) {
        classname += ' is-positioning'
      } else if (!this.l2Visible) {
        classname += ' is-hidden'
      }
      return classname
    },
    l3PanelClass () {
      let classname = ''
      if (this.light) {
        classname += ' light-theme'
      }
      if (this.l3Visible && !this.l3Ready) {
        classname += ' is-positioning'
      } else if (!this.l3Visible) {
        classname += ' is-hidden'
      }
      return classname
    },
    l2PanelStyle () {
      return {
        top: `${this.l2Top}px`,
        left: `${this.l2Left}px`,
        '--cascade-max-height': `calc(100vh - ${TOP_SAFE + this.cloudShellOffset}px)`,
        '--cascade-l2-width': `${CASCADE_L2_WIDTH}px`,
      }
    },
    l3PanelStyle () {
      return {
        top: `${this.l3Top}px`,
        left: `${this.l3Left}px`,
        '--cascade-max-height': `calc(100vh - ${TOP_SAFE + this.cloudShellOffset}px)`,
        '--cascade-l3-width': `${CASCADE_L3_WIDTH}px`,
      }
    },
  },
  watch: {
    openCloudShell () {
      this.updateLayout()
    },
    cloudShellHeight () {
      this.updateLayout()
    },
    l2Visible (visible) {
      this.$emit('cascade-open-change', visible)
    },
  },
  beforeDestroy () {
    this.clearCloseTimer()
  },
  methods: {
    getProductsGroupLabel (group) {
      if (!group) return ''
      const key = `scope.products_group.${group}`
      return this.$te(key) ? this.$t(key) : group
    },
    openL1 (item, triggerEl) {
      this.clearCloseTimer()
      if (this.activeL1 && this.activeL1.index !== item.index) {
        this.l3Visible = false
        this.l3Ready = false
        this.activeSubGroup = null
        this.l2TriggerEl = null
        this.l2Ready = false
      }
      this.activeL1 = item
      this.l1TriggerEl = triggerEl
      this.l2Visible = true
      this.l3Visible = false
      this.activeSubGroup = null
      this.l2TriggerEl = null
      this.hoveredSubGroup = null
      this.$emit('l1-open-change', item.index)
      this.$nextTick(() => {
        this.finishLayoutL2()
      })
    },
    clickL1 (item) {
      this.$emit('active-menu-change', item)
      this.$emit('scroll-to-section', { moduleIndex: item.index })
      this.closeAll()
    },
    handleL2MouseEnter (subGroup, event) {
      this.clearCloseTimer()
      this.hoveredSubGroup = subGroup
      this.activeSubGroup = subGroup
      this.l2TriggerEl = event.currentTarget
      this.l3Visible = true
      this.l3Ready = false
      this.$nextTick(() => {
        this.finishLayoutL3()
      })
    },
    handleL2Click (subGroup) {
      if (!this.activeL1) return
      const link = getFirstL3Link(this.activeL1, subGroup, this.showMenu, this.getLabel)
      if (!link) return
      this.handleLinkClick(link)
    },
    handleL2PanelEnter () {
      this.clearCloseTimer()
      this.l2Visible = true
    },
    handleL2PanelLeave () {
      this.hoveredSubGroup = null
      this.scheduleCloseCheck()
    },
    handleL3PanelEnter () {
      this.clearCloseTimer()
      this.l2Visible = true
      this.l3Visible = true
    },
    handleL3PanelLeave () {
      this.scheduleCloseCheck()
    },
    handleLinkClick (link) {
      this.closeAll()
      this.$emit('router-change', link.menu)
    },
    handleStar (e, link) {
      e.stopPropagation()
      let list = this.staredList
      if (list.includes(link.path)) {
        list = list.filter(item => item !== link.path)
      } else {
        list = [link.path, ...list]
      }
      this.$emit('update-stared-menu', list)
    },
    layoutL2 (reveal) {
      const panel = this.$refs.l2Panel
      const trigger = this.l1TriggerEl
      if (!panel || !trigger || !this.l2Visible) return

      const l1Rect = trigger.getBoundingClientRect()
      this.l2Left = calcPanelLeftFromRect(l1Rect)
      const height = panel.offsetHeight || this.l2Height
      let top = calcPanelTop({
        triggerEl: trigger,
        panelHeight: Math.min(height, this.l2Height),
        triggerHeight: TRIGGER_HEIGHT_L1,
        openCloudShell: this.openCloudShell,
        cloudShellHeight: this.cloudShellHeight,
      })
      top = clampPanelTop(panel, top, this.openCloudShell, this.cloudShellHeight)
      this.l2Top = top
      applyPanelMaxHeight(panel, this.openCloudShell, this.cloudShellHeight, '.cascade-l2-panel')

      if (reveal) {
        this.l2Ready = true
      }
    },
    layoutL3 (reveal) {
      const panel = this.$refs.l3Panel
      const l2Panel = this.$refs.l2Panel
      const trigger = this.l2TriggerEl
      if (!panel || !trigger || !this.l3Visible) return

      if (l2Panel) {
        const l2Rect = l2Panel.getBoundingClientRect()
        this.l3Left = calcPanelLeftFromRect(l2Rect)
      }
      const height = panel.offsetHeight || this.l3Height
      let top = calcPanelTop({
        triggerEl: trigger,
        panelHeight: Math.min(height, this.l3Height),
        triggerHeight: TRIGGER_HEIGHT_L2,
        openCloudShell: this.openCloudShell,
        cloudShellHeight: this.cloudShellHeight,
      })
      top = clampPanelTop(panel, top, this.openCloudShell, this.cloudShellHeight)
      this.l3Top = top
      applyPanelMaxHeight(panel, this.openCloudShell, this.cloudShellHeight, '.cascade-l3-panel-inner')

      if (reveal) {
        this.l3Ready = true
      }
    },
    updateLayout () {
      if (this.l2Visible) {
        this.layoutL2(false)
      }
      if (this.l3Visible) {
        this.layoutL3(false)
      }
    },
    finishLayoutL2 () {
      const attempt = () => {
        if (!this.l2Visible || !this.$refs.l2Panel) return false
        this.layoutL2(true)
        return true
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (attempt()) return
          setTimeout(() => {
            if (attempt()) return
            this.l2Ready = true
          }, 50)
        })
      })
    },
    finishLayoutL3 () {
      const attempt = () => {
        if (!this.l3Visible || !this.$refs.l3Panel) return false
        this.layoutL3(true)
        return true
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (attempt()) return
          setTimeout(() => {
            if (attempt()) return
            this.l3Ready = true
          }, 50)
        })
      })
    },
    isL1Hovered () {
      return !!(this.l1TriggerEl && this.l1TriggerEl.matches(':hover'))
    },
    isL2PanelHovered () {
      const panel = this.$refs.l2Panel
      return !!(panel && panel.matches(':hover'))
    },
    isL3PanelHovered () {
      const panel = this.$refs.l3Panel
      return !!(panel && panel.matches(':hover'))
    },
    shouldKeepOpen () {
      return this.isL1Hovered() ||
        this.isL2PanelHovered() ||
        this.isL3PanelHovered()
    },
    scheduleCloseCheck () {
      this.clearCloseTimer()
      const delayMs = CASCADE_MOUSE_LEAVE_DELAY * 1000
      this._closeTimer = setTimeout(() => {
        this._closeTimer = null
        if (this.shouldKeepOpen()) return
        this.closeAll()
      }, delayMs)
    },
    clearCloseTimer () {
      if (this._closeTimer) {
        clearTimeout(this._closeTimer)
        this._closeTimer = null
      }
    },
    closeAll () {
      this.clearCloseTimer()
      this.l2Visible = false
      this.l3Visible = false
      this.l2Ready = false
      this.l3Ready = false
      this.activeL1 = null
      this.l1TriggerEl = null
      this.activeSubGroup = null
      this.l2TriggerEl = null
      this.hoveredSubGroup = null
      this.$emit('l1-open-change', null)
    },
    onL1MouseLeave () {
      this.scheduleCloseCheck()
    },
  },
}
</script>

<style lang="less">
@import "../../../src/styles/less/theme";

.cascade-layer-panel {
  position: fixed;
  z-index: 100;
  opacity: 1;
  visibility: visible;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &.is-positioning {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: none;
  }

  &.is-hidden {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
}

.menu-l2-popover {
  padding: 0;
  z-index: 100 !important;
  background-color: @sidebar-dark-text-bg-color;
  border-radius: 3px;
  overflow: hidden;
  width: var(--cascade-l2-width, 180px);
  box-sizing: border-box;

  .cascade-l2-panel {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: var(--cascade-max-height, calc(100vh - 62px));
  }

  .cascade-l2-list {
    padding: 10px 0;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    background-color: @sidebar-dark-text-bg-color;
  }

  .cascade-menu-item {
    padding: 0 0 4px 0;

    .cascade-menu-link {
      height: 32px;
      line-height: 32px;
      padding: 0 8px 0 12px;
      min-width: 0;
      overflow: hidden;
      color: @sidebar-dark-text-color !important;
      cursor: pointer;
      font-size: 14px;
      border-radius: 2px;
      transition: color 0.15s, background-color 0.15s;
    }
    .cascade-menu-label {
      min-width: 0;
      font-size: 14px;
    }
    .cascade-menu-arrow {
      flex-shrink: 0;
      width: 14px;
      text-align: right;
      opacity: 1;
      color: @sidebar-dark-arrow-color;
      .anticon {
        color: inherit;
      }
    }
    &.active,
    &:hover {
      .cascade-menu-link {
        color: @sidebar-light-active-text-color !important;
        background-color: rgba(255, 255, 255, 0.06);
      }
      .cascade-menu-arrow {
        color: inherit;
      }
    }
  }

  &.light-theme {
    background-color: @cascade-light-panel-bg;
    border: 1px solid @cascade-light-panel-border;
    box-shadow: @cascade-light-panel-shadow;
    .cascade-l2-list {
      background-color: @cascade-light-panel-bg;
    }
    .cascade-menu-item {
      .cascade-menu-link {
        color: @sidebar-light-text-color !important;
      }
      .cascade-menu-arrow {
        color: @sidebar-light-arrow-color;
      }
      &.active,
      &:hover {
        .cascade-menu-link {
          color: @primary-color !important;
          background-color: @primary-1;
        }
        .cascade-menu-arrow {
          color: inherit;
        }
      }
    }
  }
}

.menu-l3-popover {
  padding: 0;
  z-index: 101 !important;
  background-color: @sidebar-dark-text-bg-color;
  border-radius: 3px;
  overflow: hidden;
  width: var(--cascade-l3-width, 614px);

  .cascade-l3-panel-inner {
    padding: 10px 0;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: var(--cascade-max-height, calc(100vh - 62px));
  }

  .cascade-l3-group {
    &:not(:first-child) {
      padding-top: 4px;
    }
  }

  .cascade-l3-group-title {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);
    line-height: 28px;
    padding: 0 16px 2px 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cascade-l3-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0 16px 4px 20px;
  }

  .cascade-l3-cell {
    min-width: 0;
    padding: 0 8px 0 0;
    font-size: 14px;
    line-height: 32px;
  }

  .cascade-l3-link {
    height: 32px;
    line-height: 32px;
    padding: 8px;
    font-size: 14px;
    color: @sidebar-dark-text-color;
    border-radius: 2px;
    cursor: pointer;
    transition: all ease-in-out 0.1s;
    min-width: 0;

    .link {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .icon {
      opacity: 0;
      flex: 0 0 14px;
      font-size: 14px;
      color: var(--antd-wave-shadow-color);
      font-weight: bolder;
      transition: all ease-in-out 0.1s;
    }

    &:hover {
      color: @sidebar-light-active-text-color !important;
      background-color: rgba(255, 255, 255, 0.06);

      .icon {
        opacity: 1;
      }
    }

    &.stared .icon {
      opacity: 1;
    }
  }

  &.light-theme {
    background-color: @cascade-light-panel-bg;
    border: 1px solid @cascade-light-panel-border;
    box-shadow: @cascade-light-panel-shadow-l3;
    .cascade-l3-group-title {
      color: rgba(0, 0, 0, 0.45);
    }
    .cascade-l3-link {
      color: #666;

      &:hover {
        color: #666;
        background: #ebebeb;
      }
    }
  }
}
</style>
